/** Toolbar Refresh control: reload the grid now, or pick an auto-refresh period.
 *
 *  Split control — the Refresh icon re-runs the current page with the current
 *  filters; the narrow arrow beside it opens the period menu (Off / 10s / 15s /
 *  30s / 45s / 1m). While auto-refresh is on, the time to the next refresh
 *  shows twice: a ring around the icon that drains over the period, and a
 *  seconds countdown in the arrow button. Pages without a period setter
 *  (Find & Replace — its results are pinned on purpose) get the icon alone.
 *
 *  State lives in `useGridAutoRefresh`; `gridRefreshToolbar()` builds this
 *  control's props from the grid's query. The 1s countdown ticker is local, so
 *  only this button re-renders — never the grid.
 */
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckIcon from '@mui/icons-material/Check';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { keyframes } from '@mui/material/styles';
import { useEffect, useState, type MouseEvent } from 'react';

import { HelpLink } from '@/components/HelpLink';

import { AUTO_REFRESH_PERIODS_MS } from './useGridAutoRefresh';

export interface GridRefreshToolbarState {
  onRefresh: () => void;
  /** The grid's query is fetching (manual click or a poll) — the icon spins. */
  refreshing: boolean;
  /** The grid's query is disabled (no search yet, preset loading, …). */
  disabled?: boolean;
  /** Replaces the default "Refresh" tooltip. */
  tooltip?: string;
  /** Auto-refresh. Omitted → manual refresh only, no period menu. */
  auto?: {
    /** 0 = off. */
    intervalMs: number;
    /** The audit call for a pick is in flight — the menu is locked. */
    pending: boolean;
    setIntervalMs: (ms: number) => void;
    /** Why polling is suspended right now, if it is. */
    pausedReason: 'error' | 'extended' | null;
    /** Countdown anchor (epoch ms): the next refresh is due a whole number of
     *  periods after it. */
    anchorMs: number;
  };
}

/** "10s", "1m" — the compact form used in labels and tooltips. */
export function formatPeriod(ms: number): string {
  return ms % 60_000 === 0 ? `${ms / 60_000}m` : `${ms / 1000}s`;
}

function periodMenuLabel(ms: number): string {
  if (ms === 60_000) return 'Every minute';
  return `Every ${ms / 1000} seconds`;
}

/** Just past a due tick, hold the countdown at 0 this long instead of wrapping
 *  to a full period: TanStack's timer can trail ours (on the first period it is
 *  armed only after React commits the new interval, measured at up to a few
 *  hundred ms), and wrapping first would flash a full ring right before the
 *  refresh spinner replaces it. */
const DUE_GRACE_MS = 1000;

/** Milliseconds until the next refresh. Ticks fire every `periodMs` from the
 *  anchor (TanStack's setInterval), including ones skipped in a hidden tab —
 *  hence the modulo rather than a plain `anchor + period - now`. */
export function msUntilNextRefresh(now: number, anchorMs: number, periodMs: number): number {
  const elapsed = Math.max(0, now - anchorMs);
  const into = elapsed % periodMs;
  if (elapsed >= periodMs && into < DUE_GRACE_MS) return 0;
  return periodMs - into;
}

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const RING_SIZE = 26;

/** Re-render once per countdown second while `active` and the tab is visible;
 *  aligned to the countdown's own second boundaries so the label never lags. */
function useCountdownNow(active: boolean, anchorMs: number, periodMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  const [visible, setVisible] = useState(() => !document.hidden);

  useEffect(() => {
    const onVisibility = () => {
      setVisible(!document.hidden);
      setNow(Date.now());
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  useEffect(() => {
    if (!active || !visible || periodMs <= 0) return undefined;
    const delay = msUntilNextRefresh(Date.now(), anchorMs, periodMs) % 1000 || 1000;
    const t = window.setTimeout(() => setNow(Date.now()), delay);
    return () => window.clearTimeout(t);
  }, [active, visible, anchorMs, periodMs, now]);

  return now;
}

export function GridRefreshControl({
  onRefresh,
  refreshing,
  disabled = false,
  tooltip,
  auto,
}: GridRefreshToolbarState) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const intervalMs = auto?.intervalMs ?? 0;
  const enabled = intervalMs > 0;
  const paused = enabled && auto?.pausedReason != null;
  const counting = enabled && !paused && !refreshing;

  const now = useCountdownNow(counting, auto?.anchorMs ?? 0, intervalMs);
  const remainingMs = counting ? msUntilNextRefresh(now, auto?.anchorMs ?? 0, intervalMs) : 0;
  const remainingS = Math.ceil(remainingMs / 1000);

  const open = (e: MouseEvent<HTMLElement>) => setAnchor(e.currentTarget);
  const close = () => setAnchor(null);
  const pick = (ms: number) => {
    auto?.setIntervalMs(ms);
    close();
  };

  let refreshTitle = tooltip ?? 'Refresh';
  if (enabled && !tooltip) {
    if (auto?.pausedReason === 'error') {
      refreshTitle = 'Auto-refresh paused: the last refresh failed. Refresh to resume.';
    } else if (auto?.pausedReason === 'extended') {
      refreshTitle =
        'Auto-refresh paused: the longer-timeout search is never repeated automatically. Refresh to re-run it once.';
    } else if (counting) {
      refreshTitle = `Refresh now (auto-refresh every ${formatPeriod(intervalMs)}, next in ${remainingS}s)`;
    } else {
      refreshTitle = `Refresh now (auto-refresh every ${formatPeriod(intervalMs)})`;
    }
  }

  const autoLabel = enabled ? `every ${formatPeriod(intervalMs)}` : 'off';

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Tooltip title={refreshTitle}>
        {/* span: a disabled button can't anchor a tooltip */}
        <span>
          <IconButton
            size="small"
            aria-label="Refresh"
            onClick={onRefresh}
            disabled={disabled}
            color={enabled && !paused ? 'primary' : 'default'}
            sx={{ position: 'relative' }}
          >
            {counting && (
              <>
                {/* Track + draining ring: full right after a refresh, empty at the next. */}
                <CircularProgress
                  variant="determinate"
                  value={100}
                  size={RING_SIZE}
                  thickness={2.5}
                  aria-hidden
                  sx={{ position: 'absolute', color: 'divider' }}
                />
                <CircularProgress
                  variant="determinate"
                  value={(remainingMs / intervalMs) * 100}
                  size={RING_SIZE}
                  thickness={2.5}
                  aria-hidden
                  data-testid="refresh-countdown-ring"
                  sx={{ position: 'absolute' }}
                />
              </>
            )}
            <RefreshIcon
              fontSize="small"
              sx={{
                ...(counting ? { transform: 'scale(0.8)' } : {}),
                ...(refreshing
                  ? {
                      animation: `${spin} 1s linear infinite`,
                      '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                    }
                  : {}),
              }}
            />
          </IconButton>
        </span>
      </Tooltip>
      {auto && (
        <>
          <Tooltip title={`Auto-refresh: ${autoLabel}`}>
            <Button
              size="small"
              onClick={open}
              aria-label={`Auto-refresh: ${autoLabel}`}
              aria-haspopup="menu"
              aria-expanded={anchor ? 'true' : undefined}
              color={enabled && !paused ? 'primary' : 'inherit'}
              sx={{
                minWidth: 0,
                px: 0.25,
                py: 0.25,
                // Off: tuck the bare arrow against the icon. On: the ring needs
                // its room — the label must not sit on it.
                ml: enabled ? 0 : -0.5,
                color: paused ? 'warning.main' : enabled ? undefined : 'action.active',
                fontVariantNumeric: 'tabular-nums',
                textTransform: 'none',
              }}
            >
              {enabled && (
                <Typography
                  component="span"
                  variant="caption"
                  data-testid="refresh-countdown-label"
                  sx={{ minWidth: '3ch', textAlign: 'left', fontWeight: 500 }}
                >
                  {paused ? 'Paused' : counting ? `${remainingS}s` : formatPeriod(intervalMs)}
                </Typography>
              )}
              <ArrowDropDownIcon fontSize="small" />
            </Button>
          </Tooltip>
          <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close}>
            <Box sx={{ px: 2, pt: 0.5, pb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="overline" color="text.secondary">
                Auto-refresh
              </Typography>
              <HelpLink story="ui-data-grids" />
            </Box>
            {[0, ...AUTO_REFRESH_PERIODS_MS].map((ms) => (
              <MenuItem
                key={ms}
                selected={ms === intervalMs}
                disabled={auto.pending}
                onClick={() => pick(ms)}
              >
                <ListItemIcon>{ms === intervalMs ? <CheckIcon fontSize="small" /> : null}</ListItemIcon>
                <ListItemText primary={ms === 0 ? 'Off' : periodMenuLabel(ms)} />
              </MenuItem>
            ))}
            <Typography
              variant="caption"
              color="text.secondary"
              component="p"
              sx={{ px: 2, pt: 1, pb: 0.5, maxWidth: 240 }}
            >
              Pauses while this tab is hidden. Turning it on is recorded in the audit log.
            </Typography>
          </Menu>
        </>
      )}
    </Box>
  );
}
